// FILENAME: frontend/src/context/SearchContext.jsx
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { PROGRAMS } from '../constants/programs';
import axios from 'axios';
import { loadOfflineSearch, saveOfflineSearch } from '../utils/offlineCache';

const SearchContext = createContext();

export const useSearch = () => useContext(SearchContext);

const splitSearchTerms = (value) => {
  if (!value) return [];
  return value
    .split(',')
    .map((term) => term.trim())
    .filter((term) => term.length > 0);
};

const SEARCH_SOURCES_STORAGE_KEY = 'gmer.search.sources';
const SEARCH_SOURCES_PARAM = 'searchSources';
const DEFAULT_SEARCH_SOURCES = { dpe: true, appsheet: false };

const normalizeSearchSource = (value) => String(value || '').trim().toLowerCase();

const parseSearchSourcesParam = (raw) => {
  if (!raw) return null;
  const tokens = String(raw)
    .split(',')
    .map(normalizeSearchSource)
    .filter(Boolean);
  if (!tokens.length) return null;
  const includeDpe = tokens.includes('dpe');
  const includeAppsheet = tokens.includes('appsheet') || tokens.includes('envolve');
  if (!includeDpe && !includeAppsheet) return null;
  return { dpe: includeDpe, appsheet: includeAppsheet };
};

const normalizeSearchSources = (sources) => ({
  dpe: Boolean(sources?.dpe),
  appsheet: Boolean(sources?.appsheet),
});

const resolveSearchSources = (sources) => {
  const normalized = normalizeSearchSources(sources);
  if (normalized.dpe || normalized.appsheet) return normalized;
  return DEFAULT_SEARCH_SOURCES;
};

const toSearchSourcesParam = (sources) => {
  const tokens = [];
  if (sources?.dpe) tokens.push('dpe');
  if (sources?.appsheet) tokens.push('appsheet');
  return tokens.join(',');
};

const loadStoredSearchSources = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(SEARCH_SOURCES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return resolveSearchSources(parsed);
  } catch (_err) {
    return null;
  }
};

const saveStoredSearchSources = (sources) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(SEARCH_SOURCES_STORAGE_KEY, JSON.stringify(resolveSearchSources(sources)));
  } catch (_err) {
    // noop
  }
};

export const SearchProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSourcesState, setSearchSourcesState] = useState(() => {
    const fromUrl = parseSearchSourcesParam(searchParams.get(SEARCH_SOURCES_PARAM));
    if (fromUrl) return resolveSearchSources(fromUrl);
    const fromStorage = loadStoredSearchSources();
    return fromStorage || DEFAULT_SEARCH_SOURCES;
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const queryClient = useQueryClient();
  const syncingFromUrlRef = useRef(false);
  const syncSourcesToUrlRef = useRef(searchParams.has(SEARCH_SOURCES_PARAM));
  const lastSourcesParamRef = useRef(searchParams.get(SEARCH_SOURCES_PARAM) || '');

  const setSearchSources = useCallback((nextSources, options = {}) => {
    const normalized = resolveSearchSources(nextSources);
    if (options.userInitiated !== false) {
      syncSourcesToUrlRef.current = true;
    }
    setSearchSourcesState(normalized);
  }, []);

  const runLocalFallbackSearch = useCallback(async (trimmed) => {
    if (!searchSourcesState.dpe) return { terms: [], results: [], suggestions: [] };
    const terms = splitSearchTerms(trimmed);
    if (terms.length === 0) return { terms: [], results: [], suggestions: [] };

    const termDescriptors = terms.map((term, index) => ({
      index,
      original: term,
      normalized: term.toLowerCase(),
      matches: [],
    }));

    for (const programName of PROGRAMS) {
      const queryKey = ['enrollmentData', { type: 'program', value: programName }];
      let programData = queryClient.getQueryData(queryKey);

      if (!programData) {
        // Offline-first: if the API is down, avoid fetching dozens of programs; only search what we already have.
        continue;
      }

      programData.forEach((report) => {
        const bacValue = report?.bac != null ? String(report.bac).toLowerCase() : '';
        const dealerName = report?.dealerName ? report.dealerName.toLowerCase() : '';

        termDescriptors.forEach((descriptor) => {
          if (bacValue.includes(descriptor.normalized) || dealerName.includes(descriptor.normalized)) {
            descriptor.matches.push({
              ...report,
              programName,
              matchedTerm: descriptor.original,
              matchedTermIndex: descriptor.index,
            });
          }
        });
      });
    }

    const combinedResults = termDescriptors.flatMap((descriptor) => descriptor.matches);
    const suggestionSet = new Set();
    const localSuggestions = [];
    combinedResults.slice(0, 50).forEach((row) => {
      const bac = row?.bac ? String(row.bac).trim() : '';
      const dealerName = row?.dealerName ? String(row.dealerName).trim() : '';
      [bac && { type: 'bac', value: bac, label: bac }, dealerName && { type: 'dealer', value: dealerName, label: dealerName }]
        .filter(Boolean)
        .forEach((item) => {
          const key = `${item.type}::${item.value.toLowerCase()}`;
          if (suggestionSet.has(key)) return;
          suggestionSet.add(key);
          localSuggestions.push(item);
        });
    });

    return { terms, results: combinedResults, suggestions: localSuggestions.slice(0, 12) };
  }, [queryClient, searchSourcesState.dpe]);

  const performSearch = useCallback(
    async (inputTerm) => {
      const rawValue = inputTerm ?? searchTerm ?? '';
      const trimmed = rawValue.trim();
      if (!trimmed) return;

      const sourcesParam = toSearchSourcesParam(searchSourcesState);
      setSearchTerm(trimmed);
      setIsSearching(true);
      setHasSearched(true);
      setSearchError(null);

      try {
        const { data } = await axios.get('/api/search/advanced', {
          params: { q: trimmed, limit: 400, sources: sourcesParam },
          // Avoid cached 304-ish behavior when SW is in play.
          headers: { 'Cache-Control': 'no-cache' },
        });
        const results = Array.isArray(data?.results) ? data.results : [];
        const nextSuggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
        setSearchResults(results);
        setSuggestions(nextSuggestions);
        setIsSearching(false);
        if (searchSourcesState.dpe && !searchSourcesState.appsheet) {
          saveOfflineSearch({ query: trimmed, results, suggestions: nextSuggestions }).catch(() => {});
        }
        return;
      } catch (error) {
        console.warn('[Search] backend search failed, falling back to local cache search', error?.message || error);
      }

      if (searchSourcesState.dpe && !searchSourcesState.appsheet) {
        try {
          const offline = await loadOfflineSearch(trimmed);
          if (offline?.results?.length) {
            setSearchResults(offline.results);
            setSuggestions(Array.isArray(offline.suggestions) ? offline.suggestions : []);
            setIsSearching(false);
            return;
          }
        } catch (_err) {
          // noop
        }
      }

      const local = await runLocalFallbackSearch(trimmed);
      setSearchResults(local.results);
      setSuggestions(local.suggestions);
      setIsSearching(false);
    },
    [searchTerm, runLocalFallbackSearch, searchSourcesState]
  );

  // URL <-> state sync for global search.
  // - Reads `?search=` and automatically executes the search (SmartSearch does not auto-run on external updates).
  // - Writes `searchTerm` back to `?search=` (replace=true to avoid polluting browser history while typing).
  useEffect(() => {
    if (location.pathname !== '/') return;
    const urlSearch = searchParams.get('search');
    const next = urlSearch != null ? String(urlSearch).trim() : '';
    if (!next) return;
    if (next === searchTerm.trim()) return;

    syncingFromUrlRef.current = true;
    setSearchTerm(next);
    // Execute immediately so opening `/?search=...` shows results without user interaction.
    void performSearch(next);
    // Release the guard on the next tick.
    queueMicrotask(() => {
      syncingFromUrlRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, searchParams]);

  useEffect(() => {
    if (location.pathname !== '/') return;
    const raw = searchParams.get(SEARCH_SOURCES_PARAM) || '';
    if (raw === lastSourcesParamRef.current) return;
    lastSourcesParamRef.current = raw;
    const fromUrl = parseSearchSourcesParam(raw);
    if (!fromUrl) return;
    const normalized = resolveSearchSources(fromUrl);
    syncSourcesToUrlRef.current = true;
    setSearchSourcesState((prev) => {
      if (prev.dpe === normalized.dpe && prev.appsheet === normalized.appsheet) return prev;
      return normalized;
    });
  }, [location.pathname, searchParams]);

  useEffect(() => {
    saveStoredSearchSources(searchSourcesState);
  }, [searchSourcesState]);

  useEffect(() => {
    if (location.pathname !== '/') return;
    if (syncingFromUrlRef.current) return;
    const trimmed = String(searchTerm || '').trim();
    const current = searchParams.get('search');
    const currentTrimmed = current != null ? String(current).trim() : '';
    const nextParams = new URLSearchParams(searchParams);
    let shouldUpdate = false;

    if (trimmed !== currentTrimmed) {
      if (trimmed) nextParams.set('search', trimmed);
      else nextParams.delete('search');
      shouldUpdate = true;
    }

    if (syncSourcesToUrlRef.current) {
      const nextSources = toSearchSourcesParam(searchSourcesState);
      const currentSources = searchParams.get(SEARCH_SOURCES_PARAM) || '';
      if (nextSources !== currentSources) {
        if (nextSources) nextParams.set(SEARCH_SOURCES_PARAM, nextSources);
        else nextParams.delete(SEARCH_SOURCES_PARAM);
        shouldUpdate = true;
      }
    }

    if (!shouldUpdate) return;
    setSearchParams(nextParams, { replace: true });
  }, [location.pathname, searchParams, searchTerm, searchSourcesState, setSearchParams]);

  useEffect(() => {
    if (!hasSearched) return;
    const trimmed = String(searchTerm || '').trim();
    if (!trimmed) return;
    void performSearch(trimmed);
  }, [hasSearched, performSearch, searchSourcesState, searchTerm]);

  const fetchSuggestions = useCallback(async (inputTerm) => {
    const trimmed = String(inputTerm || '').trim();
    if (!trimmed) return [];
    try {
      const sourcesParam = toSearchSourcesParam(searchSourcesState);
      const { data } = await axios.get('/api/search/advanced', {
        params: { q: trimmed, limit: 25, sources: sourcesParam },
        headers: { 'Cache-Control': 'no-cache' },
      });
      const items = Array.isArray(data?.suggestions) ? data.suggestions : [];
      return items.slice(0, 12);
    } catch (_err) {
      return [];
    }
  }, [searchSourcesState]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults([]);
    setHasSearched(false);
    setSearchError(null);
    setSuggestions([]);
  }, []);

  const value = useMemo(() => ({
    searchTerm,
    setSearchTerm,
    searchSources: searchSourcesState,
    setSearchSources,
    searchResults,
    suggestions,
    searchError,
    isSearching,
    hasSearched,
    performSearch,
    fetchSuggestions,
    clearSearch,
  }), [
    searchTerm,
    searchSourcesState,
    setSearchSources,
    searchResults,
    suggestions,
    searchError,
    isSearching,
    hasSearched,
    performSearch,
    fetchSuggestions,
    clearSearch,
  ]);

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};
